<template>
  <v-card flat outlined>
    <v-card-title v-if="this.supported">{{
      $t("machines.maintenance.service.reset.title")
    }}</v-card-title>

    <v-card-subtitle>
      <v-container v-if="!this.supported || !this.userCanServiceReset">
        <v-overlay absolute color="#fff" opacity=".9" class="text--primary">
          <v-row>
            <v-col cols="10" offset="1">
              <div class="text-center">
                <v-icon color="grey darken-3">mdi-key-variant</v-icon>
              </div>
              <h1 class="title d-flex mb-5" v-if="!this.userCanServiceReset">
                <v-icon color="primary" class="mr-2"
                  >mdi-information-outline</v-icon
                >
                {{
                  $t(
                    "machines.maintenance.service.reset.availableForServiceUsersOnly"
                  )
                }}
              </h1>
              <div v-else-if="!this.supported">
                <h1 class="title d-flex mb-5">
                  <v-icon color="primary" class="mr-2"
                    >mdi-information-outline</v-icon
                  >
                  {{ $t("machines.maintenance.service.reset.disabled") }}
                </h1>
                <p class="pl-8">
                  {{ $t("machines.messages.contactToLearnMore") }}
                  <a :href="mailToLink">{{ brokkEmail }}</a>
                </p>
              </div>
            </v-col>
          </v-row>
        </v-overlay>
      </v-container>

      <div v-if="!isSmartPowerPlus">
        <p>
          {{ $t("machines.maintenance.service.reset.description") }}
        </p>

        <v-btn v-if="checkError" class="mb-4" small @click="checkAvailability">
          {{ $t("common.actions.retry") }}
        </v-btn>

        <v-textarea
          class="mb-4"
          :label="$t('machines.maintenance.service.reset.comment')"
          :placeholder="
            $t('machines.maintenance.service.reset.commentPlaceholder')
          "
          :hint="$t('machines.maintenance.service.reset.commentHint')"
          :disabled="disabled"
          v-model="comment"
          rows="2"
          auto-grow
          persistent-hint
        />

        <v-btn
          class="pa-4"
          :disabled="disabled"
          :loading="loading"
          @click.stop="onReset"
        >
          <v-icon left>mdi-progress-upload</v-icon>
          {{ $t("machines.maintenance.service.reset.actions.send") }}
        </v-btn>

        <v-alert
          v-if="error"
          :type="error.type"
          class="mt-4 mb-4"
          elevation="2"
          border="left"
          colored-border
          dense
        >
          {{ error.message }}
        </v-alert>
      </div>
    </v-card-subtitle>

    <v-card-text v-if="isSmartPowerPlus">
      <v-card>
        <v-card-text v-if="serviceResetStatusSpp && showReportStatus">
          <v-row no-gutters>
            <v-col class="text-subtitle-2 mx-1 mb-2">
              {{
                $t("machines.maintenance.service.reset.reportedStatus")
              }}</v-col
            >
          </v-row>
          <v-row no-gutters>
            <v-col class="mx-1">
              <v-text-field
                :label="$t('machines.maintenance.service.reset.serviceType')"
                :value="serviceResetStatusSpp.serviceType"
                readonly
              ></v-text-field>
            </v-col>
            <v-col class="mx-1">
              <v-text-field
                :label="$t('machines.labels.engineHours')"
                :value="serviceResetStatusSpp.engineHours"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col class="mx-1">
              <v-text-field
                :label="$t('machines.maintenance.service.schedule.received')"
                :value="formatDateTime(serviceResetStatusSpp.appliedTime)"
                readonly
              ></v-text-field>
            </v-col>
            <v-col class="d-flex align-center mx-1">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="text-uppercase">
                    {{ serviceResetStatusSpp.status }}
                    <v-icon :color="serviceResetStatusColor">{{
                      serviceResetStatusIcon
                    }}</v-icon>
                  </span>
                </template>
                <div v-for="(msg, ix) in serviceResetStatusText" :key="ix">
                  {{ msg }}
                </div>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <v-card class="mt-2">
        <v-card-text>
          <v-row no-gutters>
            <v-col class="text-subtitle-2 mb-2">
              {{
                $t("machines.maintenance.service.reset.performServiceReset")
              }}</v-col
            >
          </v-row>
          <v-form v-model="valid">
            <v-row class="d-flex align-center">
              <v-col cols="12" sm="6"
                ><v-select
                  :label="$t('machines.maintenance.service.reset.serviceType')"
                  :items="serviceTypes"
                  item-value="value"
                  item-text="text"
                  v-model="selectedServiceType"
                ></v-select
              ></v-col>
              <v-col cols="12" sm="6"
                ><v-text-field
                  :label="$t('machines.labels.engineHours')"
                  v-model="engineHours"
                  type="number"
                  :rules="[validationRules.required, engineHoursRule]"
                ></v-text-field
              ></v-col>
            </v-row>
            <v-btn @click.stop="onServiceResetSpp" :disabled="loading || !valid"
              ><v-icon left>mdi-progress-upload</v-icon>
              {{ $t("machines.maintenance.service.reset.actions.send") }}</v-btn
            >
          </v-form>
        </v-card-text>
      </v-card>
      <v-alert
        v-if="serviceResetError"
        :type="serviceResetError.type"
        class="mt-4 mb-4"
        elevation="2"
        border="left"
        colored-border
        dense
      >
        {{ serviceResetError.message }}
      </v-alert>
    </v-card-text>

    <ServiceResetConfirmDialog ref="confirmDialog" />
    <ServiceResetSuccessDialog ref="successDialog" />
  </v-card>
</template>

<script>
import fetchClient from "@/utils/fetchClient"
import ValidationRules from "@/utils/validationrules"
import ServiceResetConfirmDialog from "@/dialogs/ServiceResetConfirmDialog"
import ServiceResetSuccessDialog from "@/dialogs/ServiceResetSuccessDialog"

let validationRules = null

export default {
  components: { ServiceResetSuccessDialog, ServiceResetConfirmDialog },
  props: ["machine"],

  data: () => ({
    loading: true,
    supported: false,
    waitingForYearly: false,
    available: true,
    online: true,
    checkError: null,
    resetError: null,
    comment: "",
    selectedServiceType: 2,
    engineHours: 0,
    valid: false,
    serviceResetError: null,
    serviceResetStatusSpp: null,
    showReportStatus: false, // Feature flag to hide the reported status
    intervalId: null,
    validationRules: validationRules,
  }),
  beforeCreate() {
    validationRules = ValidationRules(this.$i18n)
  },
  computed: {
    serviceTypes() {
      return [
        { text: "Service 1", value: 1 },
        { text: "Service 2", value: 2 },
        { text: "Service 3", value: 3 },
        { text: "Service 4", value: 4 },
      ]
    },
    isSmartPowerPlus() {
      return this.machine.properties?.tags?.includes("smart_power_plus")
    },
    serviceResetStatusMessageLines() {
      var msg = this.serviceResetStatusSpp?.error
      return msg?.split("\n")
    },
    serviceResetStatusColor() {
      switch (this.serviceResetStatusSpp?.status) {
        case "inactive":
          return "gray"
        case "pending":
          return "orange"
        case "applied":
          return "success"
        case "error":
          return "alert"
        default:
          return "gray"
      }
    },
    serviceResetStatusIcon() {
      switch (this.serviceResetStatusSpp?.status) {
        case "inactive":
          return "mdi-information"
        case "pending":
          return "mdi-swap-vertical-circle"
        case "applied":
          return "mdi-check-circle"
        case "error":
          return "mdi-alert-octagon"
        default:
          return ""
      }
    },
    serviceResetStatusText() {
      switch (this.serviceResetStatusSpp?.status) {
        case "inactive":
          return ["no service reset has been requested"]
        case "pending":
          return ["waiting for the machine to apply the service reset"]
        case "applied":
          return [
            "the service reset has been successfully applied on the machine",
          ]
        case "error":
          return this.serviceResetStatusMessageLines
        default:
          return []
      }
    },
    brokkEmail() {
      return this.$store.getters.brokkEmail
    },
    mailToLink() {
      return `mailto:${this.brokkEmail}?subject=Brokk Connect: Service reset for ${this.machine.productName} with serial number ${this.machine.serialNo}`
    },
    disabled() {
      return (
        this.loading ||
        !this.supported ||
        !this.online ||
        !this.available ||
        this.checkError
      )
    },
    userCanServiceReset() {
      return this.$store.getters.isService || this.$store.getters.isAdmin
    },
    error() {
      if (this.loading) {
        return null
      }

      let type = "warning"
      let message = null

      if (!this.supported) {
        message = this.$i18n.t("machines.maintenance.service.reset.disabled")
      } else if (this.waitingForYearly) {
        type = "info"
        message = this.$t(
          "machines.maintenance.service.reset.errors.waitingForYearly"
        )
      } else if (!this.available) {
        type = "info"
        message = this.$i18n.t(
          "machines.maintenance.service.reset.errors.notAvailable"
        )
      } else if (!this.online) {
        message = this.$t("machines.maintenance.service.reset.errors.offline")
      } else if (this.resetError) {
        type = "error"
        message = this.resetError
      } else if (this.checkError) {
        type = "error"
        message = this.checkError
      }

      if (message != null) {
        return { type, message }
      }

      return null
    },
  },

  async mounted() {
    await this.checkAvailability()

    if (this.isSmartPowerPlus) {
      await this.getServiceResetSpp()
      this.intervalId ??= setInterval(() => this.getServiceResetSpp(), 5000)
    }
    this.engineHours = Math.floor(this.machine.engineSeconds / 3600)
  },
  destroyed() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  },
  methods: {
    async checkAvailability() {
      this.checkError = null
      this.loading = true

      try {
        const data = await this.callApi()

        this.online = data.isOnline
        this.supported = data.serviceResetSupported
        this.available = data.serviceResetAvailable
        this.waitingForYearly = data.serviceResetWaitingForYearly
        this.comment = data.comment

        if (data.isResetting) {
          await this.waitForReset(data.resetId)
        }
      } catch (error) {
        this.checkError = error.toString()
      }

      this.loading = false
    },

    async onReset() {
      const confirm = await this.$refs.confirmDialog.open(this.machine.serialNo)
      if (!confirm) {
        return
      }

      this.resetError = null
      this.loading = true

      try {
        const data = await this.callApi("POST", null, {
          comment: this.comment,
        })
        await this.waitForReset(data.resetId)
      } catch (error) {
        this.resetError = error.toString()
      }

      this.loading = false
    },

    async waitForReset(resetId) {
      try {
        await this.callApi("GET", resetId)
        await this.$refs.successDialog.open(this.machine.serialNo)
        this.comment = ""
        this.$emit("reset")
      } catch (error) {
        this.resetError = error.toString()
      }
    },

    async callApi(method = "GET", resetId = null, data = null) {
      let url = `/api/v1/tenants/${this.machine.tenantUid}/machines/${this.machine.serialNo}/maintenance/servicereset`
      if (resetId != null) {
        url += `/${resetId}`
      }

      let errorMessage = null
      let responseData = null

      try {
        const res = await fetchClient(this.$i18n).getResponse(method, url, data)

        if (res.status === 200) {
          const txt = await res.text()
          if (txt.length > 0) {
            responseData = JSON.parse(txt)
          }
        } else {
          const error = await fetchClient(this.$i18n).getError(res)
          errorMessage = error.toString()
        }
      } catch (error) {
        errorMessage = error.toString()
      }

      if (errorMessage !== null) {
        throw new Error(errorMessage)
      }

      return responseData
    },
    async getServiceResetSpp() {
      const url = `/api/v2/tenants/${this.machine.tenantUid}/machines/${this.machine.serialNo}/maintenance/servicereset`

      try {
        this.serviceResetStatusSpp = await fetchClient(
          this.$i18n
        ).getDataOrThrow(url)
      } catch (error) {
        this.serviceResetError = { type: "error", ...error }
      } finally {
        this.loading = false
      }
    },
    async onServiceResetSpp() {
      this.loading = true
      this.serviceResetError = null
      const url = `/api/v2/tenants/${this.machine.tenantUid}/machines/${this.machine.serialNo}/maintenance/servicereset`
      const item = {
        serviceType: this.selectedServiceType,
        engineHours: parseInt(this.engineHours),
      }

      try {
        await fetchClient(this.$i18n).postOrThrow(url, item)
        await this.getServiceResetSpp()
      } catch (error) {
        this.serviceResetError = { type: "error", ...error }
      } finally {
        this.loading = false
      }
    },
    formatDateTime(appliedTime) {
      if (appliedTime) {
        return `${appliedTime.substring(11, 19)} ${appliedTime.substring(
          0,
          10
        )}`
      }
      return "-"
    },
    engineHoursRule(hours) {
      const h = parseInt(hours)
      if (h === 0 || h > 9999) {
        return this.$i18n.t("common.validation.errors.engineHoursInvalid")
      }
      return true
    },
  },
}
</script>
